/**
 * ClaimBanner — Lote Claim. CTA "Assumir conversa" quando a conversa não tem
 * dono (`assignedToUserId === null`).
 *
 * Contexto: conversas de inbound real nascem órfãs e o loop inbound→sugestão
 * só roda para o atendente atribuído (`AISuggestionListener` exige assignee).
 * Assumir destrava o fluxo. Componente puro — a mutação (`useClaimConversation`)
 * é fiada no `ConversationView`.
 *
 * ## Por que ele também olha o STATUS (13/Ago/2026)
 *
 * O banner só perguntava «tem dono?». Consultando um episódio ARQUIVADO pelo
 * fio, ele aparecia — e o botão funcionava, porque o `claim` do engine também
 * não olhava o status (corrigido no mesmo PR, e é lá que mora a autoridade).
 *
 * A trava do engine sozinha bastaria para a correção; esta aqui existe para o
 * operador não receber uma oferta que o servidor vai recusar. Oferecer e falhar
 * é pior do que não oferecer.
 */
import { isTerminalStatus } from '../format';

import type { ConversationStatus } from '@lume/shared';

export function ClaimBanner({
  assignedToUserId,
  status,
  onClaim,
  isPending,
}: {
  assignedToUserId: string | null;
  status: ConversationStatus;
  onClaim: () => void;
  isPending: boolean;
}): JSX.Element | null {
  if (assignedToUserId !== null) return null;
  // Conversa encerrada não aceita mensagem — assumir criaria um dono para um
  // trabalho que não existe, e ela entraria na worklist dele sem ter o que fazer.
  if (isTerminalStatus(status)) return null;
  return (
    <div className="banner banner--info" role="status" data-testid="claim-banner">
      <div className="banner__body">
        <strong>Conversa não atribuída.</strong> Assuma para responder e receber sugestões da IA.
      </div>
      <div className="banner__actions">
        <button
          type="button"
          onClick={onClaim}
          disabled={isPending}
          className="btn btn--primary btn--sm"
        >
          {isPending ? 'Assumindo…' : 'Assumir conversa'}
        </button>
      </div>
    </div>
  );
}
